import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { filterArrayEmpty, formatCurrency, formatDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doSetupBillingClosed,
  doSetupBillingCreditNote,
  doSetupBillingCsv,
  doSetupBillingDraftCsv,
  doSetupBillingDraftPdf,
  doSetupBillingPdf,
  doSetupBillingStatementCsv,
  doSetupBillingStatementPdf,
} from 'store/actions';
import {
  selectBillingDraftInvoices,
  selectBillingInvoices,
  selectBillingStatement,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { Invoice, InvoiceType } from 'store/remote/types';
import { pluralise } from 'lib/pluralise';
import { PageHeader } from 'components/PageHeader';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SETTINGS_BILLING
>;

export const BillingScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId);
  const draftInvoices = useSelector(selectBillingDraftInvoices) || [];
  const invoices = useSelector(selectBillingInvoices) || [];
  const statement = useSelector(selectBillingStatement)!;

  const [showAllInvoices, setShowAllInvoices] = React.useState(false);

  React.useEffect(() => {
    return () => {
      dispatch(doSetupBillingClosed());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!organisationId) {
      navigation.goBack();
    }
  }, [navigation, organisationId]);

  if (!organisationId) {
    return null;
  }

  const countCreditNotes = invoices.filter(
    (i) => i.type === InvoiceType.creditNote,
  ).length;
  const countInvoices = invoices.filter(
    (i) => i.type === InvoiceType.invoice,
  ).length;
  const hasUnpaidInvoices = invoices.some((i) => i.status !== 'paid');

  const { shownInvoices, hiddenInvoices, hiddenCreditNotes } = invoices.reduce<{
    shownInvoices: Invoice[];
    hiddenInvoices: Invoice[];
    hiddenCreditNotes: Invoice[];
  }>(
    (acc, invoice) => {
      if (
        showAllInvoices ||
        invoice.status !== 'paid' ||
        (!hasUnpaidInvoices &&
          acc.shownInvoices.length < 2 &&
          invoice.type === InvoiceType.invoice)
      ) {
        acc.shownInvoices.push(invoice);
      } else if (invoice.type === InvoiceType.creditNote) {
        acc.hiddenCreditNotes.push(invoice);
      } else {
        acc.hiddenInvoices.push(invoice);
      }
      return acc;
    },
    {
      shownInvoices: [],
      hiddenInvoices: [],
      hiddenCreditNotes: [],
    },
  );

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Invoices"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      {invoices.length ? (
        <View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: theme.padding * 2,
              paddingBottom: theme.paddingHorizontalHalf,
            }}
          >
            <Txt weight="medium" size="small" style={{ flex: 1 }}>
              {pluralise(
                countInvoices,
                '{x} Invoice',
                '{x} Invoices',
                'Invoices',
              )}
              {countCreditNotes
                ? `, ${pluralise(
                    countCreditNotes,
                    '{x} Credit Note',
                    '{x} Credit Notes',
                  )}`
                : ''}
            </Txt>
            <Txt
              size="xsmall"
              style={{
                textAlign: 'right',
                color: theme.colors.fieldValueText,
              }}
            >
              Subtotal{' • '}
              <Txt weight="medium" size="xsmall">
                Total Inc. VAT
              </Txt>
            </Txt>
          </View>
          <Card>
            {shownInvoices.map((invoice) => (
              <CardRow
                key={invoice.id}
                label={invoice.reference}
                icon={
                  invoice.type === InvoiceType.creditNote
                    ? ICONS.ReceiptCreditNoteLight
                    : ICONS.ReceiptLight
                }
                label2={`${formatDate(invoice.issueDate, UI_DATE_FORMAT_LONG)}${
                  invoice.type === InvoiceType.creditNote
                    ? ''
                    : ` • ${invoice.status === 'paid' ? 'Paid' : 'Awaiting Payment'}`
                }`}
                label2Style={{
                  color:
                    invoice.status === 'paid'
                      ? theme.colors.green
                      : invoice.dueDate < Date.now()
                        ? theme.colors.red
                        : theme.colors.fieldValueText,
                }}
                hideMenuButton
                showMenuOnPress
                menuOptions={
                  invoice.type === InvoiceType.creditNote
                    ? [
                        {
                          text: 'Preview Credit Note',
                          onPress: () =>
                            dispatch(
                              doSetupBillingCreditNote({
                                organisationId,
                                reference: invoice.reference,
                                doShare: false,
                              }),
                            ),
                        },
                        {
                          text: `${
                            Platform.OS === 'web' ? 'Download' : 'Share'
                          } Credit Note`,
                          icon: ICONS.ShareOS,
                          onPress: () =>
                            dispatch(
                              doSetupBillingCreditNote({
                                organisationId,
                                reference: invoice.reference,
                                doShare: true,
                              }),
                            ),
                        },
                      ]
                    : [
                        {
                          text: 'Preview Invoice',
                          onPress: () =>
                            dispatch(
                              doSetupBillingPdf({
                                organisationId,
                                reference: invoice.reference,
                                doShare: false,
                              }),
                            ),
                        },
                        {
                          text: `${
                            Platform.OS === 'web' ? 'Download' : 'Share'
                          } Invoice`,
                          icon: ICONS.ShareOS,
                          onPress: () =>
                            dispatch(
                              doSetupBillingPdf({
                                organisationId,
                                reference: invoice.reference,
                                doShare: true,
                              }),
                            ),
                        },
                        '-',
                        {
                          text: `${
                            Platform.OS === 'web' ? 'Download' : 'Share'
                          } CSV Breakdown`,
                          icon: ICONS.ShareOS,
                          onPress: () =>
                            dispatch(
                              doSetupBillingCsv({
                                organisationId,
                                reference: invoice.reference,
                              }),
                            ),
                        },
                      ]
                }
              >
                <Txt
                  style={{
                    textAlign: 'right',
                    color: theme.colors.fieldValueText,
                  }}
                >
                  {formatCurrency(invoice.subtotal)}
                </Txt>
                <Txt
                  style={{
                    textAlign: 'right',
                    color: theme.colors.fieldValueText,
                  }}
                  size="xsmall"
                  weight="bold"
                >
                  {formatCurrency(invoice.total)}
                </Txt>
              </CardRow>
            ))}
            {(!!hiddenInvoices.length || !!hiddenCreditNotes.length) && (
              <CardRow
                label={`Show ${[
                  pluralise(
                    hiddenInvoices,
                    '{x} other invoice',
                    '{x} other invoices',
                    '',
                  ),
                  pluralise(
                    hiddenCreditNotes,
                    '{x} credit note',
                    '{x} credit notes',
                    '',
                  ),
                ]
                  .filter(filterArrayEmpty)
                  .join(' and ')}...`}
                onPress={() => setShowAllInvoices(true)}
                labelColor={theme.colors.blue}
              />
            )}
          </Card>
        </View>
      ) : (
        <Card
          label="Invoices"
          explanation="Your organisation has no invoices yet"
        />
      )}

      {!!draftInvoices.length && (
        <View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: theme.padding * 2,
              paddingBottom: theme.paddingHorizontalHalf,
            }}
          >
            <Txt weight="medium" size="small" style={{ flex: 1 }}>
              Draft Invoices
            </Txt>
            <Txt
              size="xsmall"
              style={{
                textAlign: 'right',
                color: theme.colors.fieldValueText,
              }}
            >
              Subtotal{' • '}
              <Txt weight="medium" size="xsmall">
                Total Inc. VAT
              </Txt>
            </Txt>
          </View>
          <Card explanation="A draft invoice may change before being issued. No payments should be made until an invoice is issued">
            {draftInvoices.map((draftInvoice) => (
              <CardRow
                key={draftInvoice.key}
                label={draftInvoice.name}
                label2={
                  draftInvoice.issueDate
                    ? `Invoice will be issued ${formatDate(
                        draftInvoice.issueDate,
                        UI_DATE_FORMAT_LONG,
                      )}`
                    : undefined
                }
                icon={ICONS.ReceiptLight}
                hideMenuButton
                showMenuOnPress
                menuOptions={[
                  {
                    text: 'Preview Draft PDF Invoice',
                    onPress: () =>
                      dispatch(
                        doSetupBillingDraftPdf({
                          organisationId,
                          code: draftInvoice.code,
                          date: draftInvoice.issueDate,
                          doShare: false,
                        }),
                      ),
                  },
                  {
                    text: `${
                      Platform.OS === 'web' ? 'Download' : 'Share'
                    } Draft PDF Invoice`,
                    icon: ICONS.ShareOS,
                    onPress: () =>
                      dispatch(
                        doSetupBillingDraftPdf({
                          organisationId,
                          code: draftInvoice.code,
                          date: draftInvoice.issueDate,
                          doShare: true,
                        }),
                      ),
                  },
                  '-',
                  {
                    text: `${
                      Platform.OS === 'web' ? 'Download' : 'Share'
                    } CSV Breakdown`,
                    icon: ICONS.ShareOS,
                    onPress: () =>
                      dispatch(
                        doSetupBillingDraftCsv({
                          organisationId,
                          code: draftInvoice.code,
                          date: draftInvoice.issueDate,
                        }),
                      ),
                  },
                ]}
              >
                <Txt
                  style={{
                    textAlign: 'right',
                    color: theme.colors.fieldValueText,
                  }}
                >
                  {formatCurrency(draftInvoice.subtotal)}
                </Txt>
                <Txt
                  style={{
                    textAlign: 'right',
                    color: theme.colors.fieldValueText,
                  }}
                  size="xsmall"
                  weight="bold"
                >
                  {formatCurrency(draftInvoice.total)}
                </Txt>
              </CardRow>
            ))}
          </Card>
        </View>
      )}

      <View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: theme.padding * 2,
            paddingBottom: theme.paddingHorizontalHalf,
          }}
        >
          <Txt weight="medium" size="small" style={{ flex: 1 }}>
            Statement
          </Txt>
          <Txt
            size="xsmall"
            style={{ textAlign: 'right', color: theme.colors.fieldValueText }}
          >
            Current Balance
          </Txt>
        </View>
        <Card>
          <CardRow
            icon={ICONS.BillingStatementLight}
            label={
              !statement.count.invoices &&
              !statement.count.payments &&
              !statement.count.creditNotes
                ? 'No Activity'
                : [
                    pluralise(
                      statement.count.invoices,
                      '{x} Invoice',
                      '{x} Invoices',
                      '',
                    ),
                    pluralise(
                      statement.count.payments,
                      '{x} Payment',
                      '{x} Payments',
                      '',
                    ),
                    pluralise(
                      statement.count.creditNotes,
                      '{x} Credit Note',
                      '{x} Credit Notes',
                      '',
                    ),
                  ]
                    .filter(filterArrayEmpty)
                    .join(', ')
            }
            value={formatCurrency(statement.currentBalance)}
            hideMenuButton
            showMenuOnPress
            menuOptions={[
              {
                text: 'Preview PDF Statement',
                onPress: () =>
                  dispatch(
                    doSetupBillingStatementPdf({
                      organisationId,
                      doShare: false,
                    }),
                  ),
              },
              {
                text: `${
                  Platform.OS === 'web' ? 'Download' : 'Share'
                } PDF Statement`,
                icon: ICONS.ShareOS,
                onPress: () =>
                  dispatch(
                    doSetupBillingStatementPdf({
                      organisationId,
                      doShare: true,
                    }),
                  ),
              },
              '-',
              {
                text: `${
                  Platform.OS === 'web' ? 'Download' : 'Share'
                } CSV Statement`,
                icon: ICONS.ShareOS,
                onPress: () =>
                  dispatch(
                    doSetupBillingStatementCsv({
                      organisationId,
                    }),
                  ),
              },
            ]}
          />
        </Card>
      </View>
    </Page>
  );
};
